import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import { decodeData, encodeData } from '../../../utils/encodeAndEncode';
import { getMetadataCourseDetails, AddMyCourses } from '../../../services/userService';
import NoData from '../../../components/common/NoData';

const courseImg = '/images/course/course1.png';
const DefaultProfile = '/images/profile/default-profile.png';

// Helper function to get currency symbol
const getCurrencySymbol = (currency) => {
  switch (currency?.toUpperCase()) {
    case 'INR':
      return '₹';
    case 'USD':
      return '$';
    case 'SGD':
      return 'S$';
    case 'EUR':
      return '€';
    case 'GBP':
      return '£';
    default:
      return '$'; // Default to USD symbol
  }
};

const domain = process.env.REACT_APP_DOMAIN_URL;

function CourseDetails() {
  const navigate = useNavigate(); 
  const [expandedModules, setExpandedModules] = useState([]);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [isPaidEnrolling, setIsPaidEnrolling] = useState(false);
  const [showCourseCompletedModal, setShowCourseCompletedModal] = useState(false);

  const { encodedId } = useParams();
  const decoded = decodeData(encodedId);
  const courseId = decoded?.id;

  useEffect(() => {
    // console.log('Decoded Course ID:', courseId);
  }, [courseId]);

  const [courseData, setCourseData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Function to process course data and parse JSON fields
  const processCourseData = (data) => {
    if (!data) return data;
    
    const processed = { ...data };
    
    // Parse tags if it's a string
    if (typeof processed.tags === 'string') {
      try {
        processed.tags = JSON.parse(processed.tags);
        // console.log('Parsed tags:', processed.tags);
      } catch (error) {
        console.error('Error parsing tags:', error);
        processed.tags = [];
      }
    }
    
    // Parse course_info if it's a string
    if (typeof processed.course_info === 'string') {
      try {
        processed.course_info = JSON.parse(processed.course_info);
        // console.log('Parsed course_info:', processed.course_info);
      } catch (error) {
        console.error('Error parsing course_info:', error);
        processed.course_info = [];
      }
    }
    
    return processed;
  };

  useEffect(() => {
    if (!courseId) {
      setCourseData(null);
      setIsLoading(false);
      setLoadError('Invalid course link.');
      return;
    }

    getCourseDetails();
  }, [courseId]);

  useEffect(() => {
    const moduleNames = courseData?.modules ? Object.keys(courseData.modules) : [];
    if (!moduleNames.length) return;

    setExpandedModules((prev) => {
      if (prev.length) return prev;
      return [moduleNames[0]];
    });
  }, [courseData]);

  useEffect(() => {
    if (!courseData?.is_course_completed) return;
    setShowCourseCompletedModal(true);
  }, [courseData?.is_course_completed]);

  async function getCourseDetails() {
    try {
      setIsLoading(true);
      setLoadError('');
      // console.log('Fetching course detail......');
      const response = await getMetadataCourseDetails({
        course_id: courseId
      });

      // console.log('Course detail fetched successfully-------------:', response.data);
      
      // Process the course data to parse JSON fields
      const processedData = processCourseData(response.data);
      // console.log('Processed course data:', processedData);
      
      setCourseData(processedData);
    } catch (error) {
      console.error('Error fetching course details:', error);
      setCourseData(null);
      setLoadError('Unable to load course details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  async function EnrollFreeCourse() {
    try {
      setIsEnrolling(true);

      const response = await AddMyCourses({
        course_id: courseId
      });

      // Simulate 2-second loader
      await new Promise(resolve => setTimeout(resolve, 2000));

      // console.log('Enrolled successfully:', response);

      // Show toast after loading
      toast.success('Enrolled successfully! Redirecting...', {
        position: 'top-right',
        autoClose: 1000,
      });

      // Wait 1 second after toast before redirecting
      await new Promise(resolve => setTimeout(resolve, 1000));

      const encodedCourseId = encodeData({ id: courseId });
      navigate(`/user/courses/WatchCourse/${encodeURIComponent(encodedCourseId)}`);

    } catch (error) {
      console.error('Error enrolling in course:', error);
      toast.error('Failed to enroll in course. Please try again.');
    } finally {
      setIsEnrolling(false);
    }
  }

  const toggleModule = (moduleId) => {
    setExpandedModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handlePaidEnrollment = async () => {
    setIsPaidEnrolling(true);

    // Simulate 2-second loading
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Log the data being passed
    // Commented multi-line console.log);

    
 const targetState = {
    course_id: Number(courseId),
    course_name: courseData?.course_name || '',
    course_price: courseData?.course_price || '0',
    currency: courseData?.currency || 'USD',
    banner_image: courseData?.banner_image || '',
    course_type: courseData?.course_type || 'Paid',
    course_desc: courseData?.course_desc || '',
    discountCode: null,
    discountValue: 0,
    points: 0
  };

  if (domain === 'lms.tpi.sg') {
    navigate('/user/courses/orderDetails', { state: targetState });

  } else if ( domain === 'lms.creatorfoundation.in') {
    navigate('/user/courses/orderDetailsWithPayu', { state: targetState });

  } else if (domain === 'lms.nxgenvarsity.com') {
    navigate('/user/courses/orderDetailsWithPayu', { state: targetState });

  } else {
    navigate('/user/courses/orderDetails', { state: targetState });
  }

    setIsPaidEnrolling(false);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[var(--primary-color)] border-t-transparent" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (!courseData) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-2xl p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <Icon icon="material-symbols:error-outline" width={22} height={22} className="text-rose-500 mt-0.5" />
            <div className="min-w-0">
              <p className="font-semibold text-[var(--text-primary)]">Something went wrong</p>
              <p className="text-sm text-[var(--text-secondary)] mt-1 break-words">
                {loadError || 'Course details are not available.'}
              </p>
              <div className="flex gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => navigate('/user/courses')}
                  className="px-4 py-2 rounded-xl border border-[var(--border-color)] text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={getCourseDetails}
                  className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-[var(--primary-color)] hover:bg-[var(--primary-hover)] transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasModules = courseData.modules &&
    Object.keys(courseData.modules).length > 0;
  const isFree = courseData?.course_type?.toLowerCase() === 'free';

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-5 py-5 lg:py-7">
        {showCourseCompletedModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <button
              type="button"
              aria-label="Close"
              onClick={() => setShowCourseCompletedModal(false)}
              className="absolute inset-0 bg-black/50"
            />
            <div className="relative w-full max-w-md rounded-2xl bg-[var(--bg-primary)] border border-[var(--border-color)] shadow-lg p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <Icon icon="material-symbols:check-circle" width={22} height={22} className="text-emerald-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-[var(--text-primary)]">Course completed</h3>
                  <p className="text-sm text-[var(--text-secondary)] mt-1 leading-relaxed">
                    Great work — you’ve completed this course. You can now download your certificate (if available) or explore more courses to continue learning.
                  </p>
                  <div className="flex gap-3 mt-5">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCourseCompletedModal(false);
                        navigate('/user/certificates');
                      }}
                      className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-[var(--primary-color)] hover:bg-[var(--primary-hover)] transition-colors"
                    >
                      View Certificates
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCourseCompletedModal(false);
                        navigate('/user/courses');
                      }}
                      className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold border border-[var(--border-color)] text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors"
                    >
                      Explore Courses
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={() => navigate('/user/courses')}
          className="inline-flex items-center gap-2 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--primary-color)] transition-colors mb-6"
        >
          <Icon icon="mdi:arrow-left" width={20} height={20} />
          Back to Courses
        </button>

        <div className="rounded-2xl overflow-hidden border border-[var(--border-color)] shadow-sm mb-8">
          <div className="relative h-48 sm:h-56 bg-[var(--bg-tertiary)]">
            <img
              src={courseData.banner_image || courseImg}
              alt={courseData.course_name || 'Course'}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.src = courseImg; e.target.onerror = null; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-white drop-shadow-sm break-words">
                {courseData.course_name || 'Untitled Course'}
              </h1>
              {courseData.tags && Array.isArray(courseData.tags) && courseData.tags.length > 0 && (
                <p className="text-white/90 text-sm mt-1">{courseData.tags.join(' · ')}</p>
              )}
            </div>
          </div>
          <div className="p-5 sm:p-6 border-t border-[var(--border-color)] bg-transparent">
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-[var(--text-secondary)]">
              <span className="flex items-center gap-1.5">
                <Icon icon="material-symbols:star" className="text-amber-500" width={18} height={18} />
                {courseData?.courseMeta?.averageRating || '0.0'} rating
              </span>
              <span className="flex items-center gap-1.5">
                <Icon icon="material-symbols:menu-book" width={18} height={18} />
                {courseData?.courseMeta?.modulesCount || 0} Modules
              </span>
              <span className="flex items-center gap-1.5">
                <Icon icon="material-symbols:update" width={18} height={18} />
                {courseData?.courseMeta?.totalVideoDuration || '00:00:00'}
              </span>
              <span className="flex items-center gap-1.5">
                <Icon icon="material-symbols:school" width={18} height={18} />
                {courseData?.levels || 'All levels'}
              </span>
              <span className="flex items-center gap-1.5">
                <Icon icon="material-symbols:language" width={18} height={18} />
                {courseData?.course_language || 'Not specified'}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-4 pt-4 border-t border-[var(--border-color)]">
              <img
                src={courseData?.trainer?.profile || DefaultProfile}
                alt={courseData?.trainer?.name}
                className="w-10 h-10 rounded-full object-cover border-2 border-[var(--border-color)]"
              />
              <div>
                <p className="font-medium text-[var(--text-primary)]">By {courseData?.trainer?.name || 'Instructor'}</p>
                <p className="text-sm text-[var(--text-secondary)] mt-0.5">{courseData?.trainer?.role || 'Instructor'}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex-1 min-w-0 space-y-6">
            <div className="rounded-2xl border border-[var(--border-color)] p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Course Info</h2>
              <ul className="space-y-3">
                {courseData.course_info && Array.isArray(courseData.course_info) && courseData.course_info.length > 0 ? (
                  courseData.course_info.map((point, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Icon icon="material-symbols:check-circle" className="text-emerald-500 shrink-0 mt-0.5" width={20} height={20} />
                      <span className="text-[var(--text-secondary)]">{point}</span>
                    </li>
                  ))
                ) : (
                  <li className="flex items-start gap-3">
                    <Icon icon="material-symbols:check-circle" className="text-emerald-500 shrink-0" width={20} height={20} />
                    <span className="text-[var(--text-secondary)]">Course information not available</span>
                  </li>
                )}
              </ul>
            </div>

            {courseData.course_desc && (
              <div className="rounded-2xl border border-[var(--border-color)] p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4">About This Course</h2>
                <p className="text-[var(--text-secondary)] leading-relaxed break-words">{courseData.course_desc}</p>
              </div>
            )}

            <div className="rounded-2xl border border-[var(--border-color)] overflow-hidden shadow-sm">
              <h2 className="text-lg font-semibold text-[var(--text-primary)] p-6 pb-0">Course Content</h2>
              {hasModules ? (
                <div className="p-4 pt-4">
                  {Object.entries(courseData.modules).map(([moduleName, contents]) => (
                    <div key={moduleName} className="border border-[var(--border-color)] rounded-xl overflow-hidden mb-3 last:mb-0">
                      <button
                        type="button"
                        onClick={() => toggleModule(moduleName)}
                        className="w-full flex items-center gap-3 p-4 text-left hover:bg-[var(--bg-secondary)] transition-colors"
                      >
                        <span className="font-medium text-[var(--text-primary)] min-w-0 flex-1 truncate">{moduleName}</span>
                        <span className="ml-auto flex items-center gap-3 shrink-0">
                          <span className="text-sm text-[var(--text-secondary)] whitespace-nowrap">{contents.length} items</span>
                          <Icon
                            icon={expandedModules.includes(moduleName) ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                            width={20}
                            height={20}
                            className="text-[var(--text-secondary)]"
                          />
                        </span>
                      </button>
                      {expandedModules.includes(moduleName) && (
                        <div className="border-t border-[var(--border-color)]">
                          {contents.map((content, idx) => (
                            <div
                              key={content?.id ? `${content.type}-${content.id}` : `${content.type}-${idx}`}
                              className="flex items-center gap-3 px-4 py-3 border-b border-[var(--border-color)] last:border-b-0"
                            >
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                {content.type === 'Video' && <Icon icon="material-symbols:play-circle-outline" width={20} height={20} className="text-[var(--primary-color)] shrink-0" />}
                                {content.type === 'Document' && <Icon icon="material-symbols:description-outline" width={20} height={20} className="text-amber-500 shrink-0" />}
                                {content.type === 'Assessment' && <Icon icon="material-symbols:quiz-outline" width={20} height={20} className="text-[var(--primary-color)] shrink-0" />}
                                {content.type === 'Survey' && <Icon icon="material-symbols:analytics-outline" width={20} height={20} className="text-[var(--primary-color)] shrink-0" />}
                                <span className="text-sm text-[var(--text-primary)] truncate">{content.title || 'Untitled'}</span>
                              </div>
                              <span className="text-xs text-[var(--text-secondary)] shrink-0 text-right w-20 sm:w-24 whitespace-nowrap">
                                {content.type === 'Video' && content.duration}
                                {content.type === 'Document' && content.fileType}
                                {content.type === 'Assessment' && content.questions}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6">
                  <NoData caption="No course content available yet" />
                </div>
              )}
            </div>

            {courseData.trainer ? (
              <div className="rounded-2xl border border-[var(--border-color)] p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Instructor</h2>
                <div className="flex flex-col sm:flex-row gap-5">
                  <img
                    src={courseData?.trainer?.profile || DefaultProfile}
                    alt={courseData?.trainer?.name}
                    className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover border border-[var(--border-color)]"
                  />
                  <div>
                    <h3 className="font-semibold text-[var(--text-primary)]">{courseData?.trainer?.name || 'Instructor'}</h3>
                    <p className="text-sm text-[var(--text-secondary)] mt-0.5">{courseData?.trainer?.role || 'Instructor'}</p>
                    <p className="text-[var(--text-secondary)] text-sm mt-3 leading-relaxed">{courseData?.trainer?.bio || 'No bio available'}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-[var(--border-color)] p-6">
                <NoData caption="Instructor information not available" />
              </div>
            )}
          </div>

          <div className="w-full lg:w-[360px] shrink-0">
            <div className="lg:sticky lg:top-6 rounded-2xl border border-[var(--border-color)] shadow-sm overflow-hidden">
              <div className="aspect-video bg-[var(--bg-tertiary)]">
                <img
                  src={courseData.banner_image || courseImg}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = courseImg; e.target.onerror = null; }}
                />
              </div>
              <div className="p-5">
                {/* Price display - hidden */}
                {/* <div className="flex items-baseline justify-between gap-2 mb-4">
                  <span className="text-2xl font-bold text-[var(--text-primary)]">
                    {isFree ? 'Free' : `${getCurrencySymbol(courseData?.currency)}${courseData?.course_price || '0'}`}
                  </span>
                  {!isFree && (
                    <span className="text-sm text-[var(--text-secondary)] line-through">
                      {getCurrencySymbol(courseData?.currency)}{Number(courseData?.course_price || 0) + 5}
                    </span>
                  )}
                </div> */}

                {/* Paid enrollment hidden - treat all as free */}
                {/* {courseData?.course_type?.toLowerCase() === 'paid' ? (
                  <button
                    type="button"
                    onClick={courseData.is_course_purchase ? () => {
                      const encodedCourseId = encodeData({ id: courseId });
                      navigate(`/user/courses/WatchCourse/${encodeURIComponent(encodedCourseId)}`);
                    } : handlePaidEnrollment}
                    disabled={isPaidEnrolling}
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-white bg-[var(--primary-color)] hover:bg-[var(--primary-hover)] disabled:opacity-70 transition-colors"
                  >
                    {isPaidEnrolling ? (
                      <>
                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Icon icon={courseData.is_course_purchase ? 'mdi:play-circle' : 'mdi:lock-open'} width={22} height={22} />
                        {courseData.is_course_purchase ? 'Continue Learning' : 'Enroll Now'}
                      </>
                    )}
                  </button>
                ) : ( */}
                  <button
                    type="button"
                    onClick={courseData.is_course_purchase ? () => {
                      const encodedCourseId = encodeData({ id: courseId });
                      navigate(`/user/courses/WatchCourse/${encodeURIComponent(encodedCourseId)}`);
                    } : EnrollFreeCourse}
                    disabled={isEnrolling}
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-white bg-[var(--primary-color)] hover:bg-[var(--primary-hover)] disabled:opacity-70 transition-colors"
                  >
                    {isEnrolling ? (
                      <>
                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Enrolling...
                      </>
                    ) : (
                      <>
                        <Icon icon="mdi:play-circle" width={22} height={22} />
                        {courseData.is_course_purchase ? 'Continue Learning' : 'Enroll Now'}
                      </>
                    )}
                  </button>
                {/* )} */}}

                <p className="flex items-center justify-center gap-2 text-sm text-[var(--text-secondary)] mt-3">
                  <Icon icon="mdi:infinity" width={18} height={18} />
                  Full lifetime access
                </p>

                <h3 className="text-sm font-semibold text-[var(--text-primary)] mt-5 mb-3">This course includes</h3>
                <ul className="space-y-2.5 text-sm text-[var(--text-secondary)]">
                  {hasModules ? (
                    <>
                      <li className="flex items-center gap-2">
                        <Icon icon="material-symbols:menu-book" width={18} height={18} className="text-[var(--primary-color)]" />
                        {courseData?.courseMeta?.modulesCount || 0} Modules
                      </li>
                      <li className="flex items-center gap-2">
                        <Icon icon="mdi:video-outline" width={18} height={18} className="text-[var(--primary-color)]" />
                        {courseData?.modules && Object.values(courseData.modules).flat().filter(item => item.type === 'Video').length || 0} Videos · {courseData?.courseMeta?.totalVideoDuration || '00:00:00'}
                      </li>
                      <li className="flex items-center gap-2">
                        <Icon icon="material-symbols:quiz" width={18} height={18} className="text-[var(--primary-color)]" />
                        {courseData?.modules && Object.values(courseData.modules).flat().filter(item => item.type === 'Assessment').length || 0} Assessments
                      </li>
                      <li className="flex items-center gap-2">
                        <Icon icon="material-symbols:description-outline" width={18} height={18} className="text-[var(--primary-color)]" />
                        {courseData?.modules && Object.values(courseData.modules).flat().filter(item => item.type === 'Document').length || 0} Documents
                      </li>
                      <li className="flex items-center gap-2">
                        <Icon icon="material-symbols:analytics-outline" width={18} height={18} className="text-[var(--primary-color)]" />
                        {courseData?.modules && Object.values(courseData.modules).flat().filter(item => item.type === 'Survey').length || 0} Surveys
                      </li>
                      <li className="flex items-center gap-2">
                        <Icon icon="mdi:infinity" width={18} height={18} className="text-[var(--primary-color)]" />
                        Full lifetime access
                      </li>
                      <li className="flex items-center gap-2">
                        <Icon icon="mdi:certificate-outline" width={18} height={18} className="text-[var(--primary-color)]" />
                        Certificate of completion
                      </li>
                    </>
                  ) : (
                    <li className="text-[var(--text-light)]">Course content is being prepared</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CourseDetails;